import { formatFull } from "@so/shared";
import type { OutcomeResponse } from "../api/queries.js";
import { Alert, Badge, Button, Card } from "./ui.js";

/**
 * The written second opinion as the patient reads it.
 *
 * Laid out as a document rather than a dashboard: this is the thing they paid
 * for, will read more than once, and will hand to their own doctor. The
 * concurrence goes at the top in plain words, because it is the question they
 * actually asked.
 */
export function OpinionView({
  report,
  viewerTimezone,
}: {
  readonly report: OutcomeResponse;
  readonly viewerTimezone: string;
}) {
  const { outcome } = report;

  const tone =
    outcome.concurrence === "agree"
      ? "accent"
      : outcome.concurrence === "disagree"
        ? "warn"
        : "caution";

  return (
    <Card className="overflow-hidden">
      <header className="border-line bg-surface-2 border-b px-6 py-5 print:bg-white">
        <p className="text-accent font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
          Second opinion
        </p>
        <h2 className="mt-1.5 text-[21px] font-semibold tracking-[-0.01em]">
          {report.concurrence_label}
        </h2>
        <p className="text-ink-soft mt-2 text-[14px]">
          {outcome.authored_by_name}
          <span className="mx-1.5">·</span>
          {formatFull(outcome.authored_at, viewerTimezone)}
        </p>
      </header>

      <div className="space-y-7 px-6 py-6">
        {outcome.urgency !== "routine" && (
          <Alert tone={outcome.urgency === "urgent" ? "warn" : "caution"}>
            {report.urgency_label}
          </Alert>
        )}

        <section>
          <h3 className="text-ink-faint mb-2 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
            The case reviewed
          </h3>
          <p className="text-[15px] leading-relaxed whitespace-pre-line">{outcome.case_summary}</p>
        </section>

        {outcome.concurrence_rationale !== undefined && (
          <section>
            <h3 className="text-ink-faint mb-2 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
              Why
            </h3>
            <div className={`border-l-3 pl-4 ${tone === "warn" ? "border-warn" : "border-accent"}`}>
              <p className="text-[15px] leading-relaxed whitespace-pre-line">
                {outcome.concurrence_rationale}
              </p>
            </div>
          </section>
        )}

        <section>
          <h3 className="text-ink-faint mb-2 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
            Findings
          </h3>
          <p className="text-[15px] leading-relaxed whitespace-pre-line">{outcome.findings}</p>
        </section>

        <section>
          <h3 className="text-ink-faint mb-3 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
            What we recommend
          </h3>
          <ol className="space-y-4">
            {outcome.recommended_actions.map((item, index) => (
              <li key={index} className="flex gap-3.5">
                <span className="bg-accent-soft text-accent mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold">
                  {index + 1}
                </span>
                <div>
                  <p className="text-[15px] font-medium">{item.action}</p>
                  <p className="text-ink-soft mt-1 text-[14.5px] leading-relaxed">
                    {item.rationale}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {outcome.additional_investigations.length > 0 && (
          <section>
            <h3 className="text-ink-faint mb-2 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
              Further investigations suggested
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-[15px] leading-relaxed">
              {outcome.additional_investigations.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        {outcome.reviewed_document_keys.length > 0 && (
          <section>
            <h3 className="text-ink-faint mb-2 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
              Based on
            </h3>
            <p className="text-ink-soft text-[14px]">
              {outcome.reviewed_document_keys.length} record
              {outcome.reviewed_document_keys.length === 1 ? "" : "s"} you supplied, and the
              consultation held on {formatFull(report.consulted_at, viewerTimezone)}.
            </p>
          </section>
        )}
      </div>

      <footer className="border-line bg-surface-2 flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4 print:hidden">
        <p className="text-ink-faint max-w-md text-[12.5px] leading-relaxed">
          This is a second opinion for your consideration. Discuss it with the doctor treating you
          before changing any treatment.
        </p>
        {/* Print rather than a generated PDF: the browser already renders this
            document well, and print-to-PDF gives the patient a file to keep
            without shipping a rendering engine to do it. */}
        <Button variant="secondary" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
      </footer>
    </Card>
  );
}

export function OpinionPending({ clinicianName }: { readonly clinicianName: string }) {
  return (
    <Card className="px-6 py-8 text-center">
      <Badge tone="caution">Being written</Badge>
      <p className="mt-3 text-[15.5px] font-medium">
        {clinicianName} is preparing your second opinion
      </p>
      <p className="text-ink-soft mx-auto mt-2 max-w-md text-[14.5px] leading-relaxed">
        You will receive an email as soon as it is ready, usually within a couple of days of the
        consultation.
      </p>
    </Card>
  );
}
